import { useState, useEffect, useCallback, useMemo } from 'react'

type RemainingTimeResult = {
  remainingTime: string
  deadlineTime: string
  isExpired: boolean
  minutes: number
  seconds: number
}

type UseRemainingTimeOptions = {
  onExpire?: () => void
  format?: 'compact' | 'verbose'
}

/**
 * 残り時間と期限時刻を管理するカスタムフック
 * @param expiresAt - 期限のISO8601形式の日時文字列
 * @param options - オプション設定
 * @returns RemainingTimeResult - 残り時間・期限時刻の情報
 */
export function useRemainingTime(
  expiresAt: string,
  options: UseRemainingTimeOptions = {}
): RemainingTimeResult {
  const { onExpire, format = 'verbose' } = options

  const [remainingTime, setRemainingTime] = useState<string>('')
  const [isExpired, setIsExpired] = useState(false)
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)

  const deadlineTime = useMemo(() => {
    const date = new Date(expiresAt)
    const hours = date.getHours()
    const mins = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${mins}`
  }, [expiresAt])

  const formatTime = useCallback(
    (mins: number, secs: number): string => {
      if (format === 'compact') {
        return `${mins}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}分${secs.toString().padStart(2, '0')}秒`
    },
    [format]
  )

  useEffect(() => {
    let hasExpired = false

    const updateRemainingTime = () => {
      const expiresAtDate = new Date(expiresAt)
      const now = new Date()
      const diff = expiresAtDate.getTime() - now.getTime()

      if (diff <= 0) {
        setRemainingTime('期限切れ')
        setIsExpired(true)
        setMinutes(0)
        setSeconds(0)

        if (!hasExpired) {
          hasExpired = true
          onExpire?.()
        }
        return
      }

      const mins = Math.floor(diff / 1000 / 60)
      const secs = Math.floor((diff / 1000) % 60)

      setMinutes(mins)
      setSeconds(secs)
      setRemainingTime(formatTime(mins, secs))
    }

    updateRemainingTime()
    const interval = setInterval(updateRemainingTime, 1000)

    return () => clearInterval(interval)
  }, [expiresAt, onExpire, formatTime])

  return { remainingTime, deadlineTime, isExpired, minutes, seconds }
}
